import { Chip, Tooltip, Typography } from '@mui/material';
import moment from 'moment';

// GH-841 stage C — paid circles (GH-834 simplified). The price is in major units (80 = ₪80); the currency is
// derived from the circle's world: global → USD, Israel → ILS. 0 / empty = free. Members matching free_for
// (company / email / verified private email) get the circle free — the server computes it, never the client.

export type Currency = 'ILS' | 'USD';
export type PaymentStatus = 'free' | 'pending' | 'paid' | 'refunded' | 'refund_failed';
export type FreeFor = { companies?: (string | { _id: string; english_name?: string })[]; emails?: string[] };

const SYMBOL: Record<Currency, string> = { ILS: '₪', USD: '$' };

export const currencyOf = (c: { is_global?: boolean }): Currency => (c.is_global ? 'USD' : 'ILS');
export const currencySymbol = (currency: Currency | string | undefined) =>
	SYMBOL[(currency as Currency) || 'ILS'] ?? '';
/** ₪80 / $20 / ₪12.50 */
export const fmtMoney = (amount: number, currency: Currency | string | undefined) => {
	const n = Number(amount) || 0;
	return `${currencySymbol(currency)}${Number.isInteger(n) ? n : n.toFixed(2)}`;
};
/** ids of free_for.companies — the server may send them populated ({ _id, english_name }) or as plain ids */
export const freeForCompanyIds = (ff?: FreeFor | null) =>
	(ff?.companies || []).map((x) => String(typeof x === 'string' ? x : x?._id)).filter(Boolean);

// GH-841 stage C: the same rule as the server (supportCircles.service ADMIN_EMAIL_RE) — "<a@b.com>" is invalid on both
const EMAIL_RE = /^[^\s@,;<>()]+@[^\s@,;<>()]+\.[^\s@,;<>()]+$/;
/** textarea → emails: one per line or comma/semicolon separated; trimmed, lowercased, unique. `invalid` = what doesn't look like an email. */
export const parseEmails = (raw: string) => {
	const all = Array.from(
		new Set(
			(raw || '')
				.split(/[\s,;]+/)
				.map((s) => s.trim().toLowerCase())
				.filter(Boolean),
		),
	);
	return { emails: all.filter((e) => EMAIL_RE.test(e)), invalid: all.filter((e) => !EMAIL_RE.test(e)) };
};

/** adminList revenue: a number in the circle's currency, or { amount, currency } — accept both */
export const revenueOf = (c: {
	is_global?: boolean;
	revenue?: number | { amount?: number; currency?: string } | null;
	currency?: string;
}) => {
	const r = c.revenue;
	if (r && typeof r === 'object')
		return { amount: Number(r.amount) || 0, currency: (r.currency || c.currency || currencyOf(c)) as Currency };
	return { amount: Number(r) || 0, currency: (c.currency || currencyOf(c)) as Currency };
};

/** list "מחיר" cell: ₪80 / $20 / חינם (+ a hint when some members get it free) */
export function PriceCell({
	circle: c,
}: {
	circle: { is_global?: boolean; price?: number | null; free_for?: FreeFor | null };
}) {
	const price = Number(c.price) || 0;
	if (price <= 0) return <>חינם</>;
	const companies = c.free_for?.companies?.length || 0;
	const emails = c.free_for?.emails?.length || 0;
	return (
		<>
			{fmtMoney(price, currencyOf(c))}
			{companies || emails ? (
				<Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
					חינם ל:{' '}
					{[companies ? `${companies} חברות` : '', emails ? `${emails} מיילים` : '']
						.filter(Boolean)
						.join(' · ')}
				</Typography>
			) : null}
		</>
	);
}

/** participants dialog: why a registration ended (GH-841 stage C cancel_reason values from the server) */
const CANCEL_REASON_LABELS: Record<string, string> = {
	member: '(ביטל/ה)',
	admin: '(בוטל/ה ע״י אדמין)', // GH-856
	payment_expired: '(לא השלים/ה תשלום)',
	circle_cancelled: '(המעגל בוטל)',
	circle_cancelled_unpaid: '(המעגל בוטל לפני התשלום)',
	circle_full: '(שילם/ה אחרי שהמקום פג והמעגל התמלא — הוחזר)',
	circle_closed: '(שילם/ה אחרי שהמעגל נסגר — הוחזר)',
};
export const cancelReasonLabel = (reason?: string) =>
	CANCEL_REASON_LABELS[reason || 'member'] || CANCEL_REASON_LABELS.member;

type PaymentFields = {
	payment_status?: PaymentStatus | string;
	amount?: number;
	currency?: string;
	paid_at?: string;
	refunded_at?: string;
	hold_expires_at?: string;
	payment_id?: string;
	past_payments?: { payment_id?: string; amount?: number; currency?: string; status?: string; note?: string }[];
};

// GH-841: an earlier payment on this registration whose refund failed (the member re-registered afterwards, or a refused
// duplicate payment) — it lives in past_payments only, so it is shown and counted separately
const pastRefundFailures = (p: PaymentFields) => (p.past_payments || []).filter((x) => x?.status === 'refund_failed');
/** refunds of this participant that still need a manual PayMe refund (the current one + earlier ones) */
export const refundFailuresOf = (p: PaymentFields) =>
	(p.payment_status === 'refund_failed' ? 1 : 0) + pastRefundFailures(p).length;

/** participants dialog "תשלום" cell: שולם ₪80 / ממתין לתשלום / הוחזר / החזר נכשל / חינם */
export function PaymentChip({ p, fallbackCurrency }: { p: PaymentFields; fallbackCurrency: Currency }) {
	const past = pastRefundFailures(p);
	const chip = <CurrentPaymentChip p={p} fallbackCurrency={fallbackCurrency} />;
	if (!past.length) return chip;
	return (
		<>
			{chip}
			{past.map((x, i) => (
				<Tooltip
					key={`${x.payment_id || i}`}
					title={[
						'יש לבצע החזר ידני ב-PayMe',
						x.payment_id ? `תשלום: ${x.payment_id}` : '',
						x.note === 'duplicate' ? 'תשלום כפול' : '',
					]
						.filter(Boolean)
						.join(' · ')}
				>
					<Chip
						size="small"
						color="error"
						sx={{ mr: 0.5 }}
						label={`החזר קודם נכשל${x.amount ? ` ${fmtMoney(x.amount, x.currency || p.currency || fallbackCurrency)}` : ''}`}
					/>
				</Tooltip>
			))}
		</>
	);
}

function CurrentPaymentChip({ p, fallbackCurrency }: { p: PaymentFields; fallbackCurrency: Currency }) {
	const cur = (p.currency || fallbackCurrency) as Currency;
	const amount = p.amount ? ` ${fmtMoney(p.amount, cur)}` : '';
	const tip = [
		p.paid_at ? `שולם: ${moment(p.paid_at).format('DD/MM/YYYY HH:mm')}` : '',
		p.refunded_at ? `הוחזר: ${moment(p.refunded_at).format('DD/MM/YYYY HH:mm')}` : '',
		p.hold_expires_at ? `המקום שמור עד ${moment(p.hold_expires_at).format('DD/MM HH:mm')}` : '',
		p.payment_id ? `תשלום: ${p.payment_id}` : '',
	]
		.filter(Boolean)
		.join(' · ');
	let chip: JSX.Element;
	switch (p.payment_status) {
		case 'paid':
			chip = <Chip size="small" color="success" label={`שולם${amount}`} />;
			break;
		case 'pending': {
			const expired = !!p.hold_expires_at && moment().isAfter(moment(p.hold_expires_at));
			chip = (
				<Chip
					size="small"
					color="warning"
					variant="outlined"
					label={expired ? 'ממתין לתשלום (פג)' : 'ממתין לתשלום'}
				/>
			);
			break;
		}
		case 'refunded':
			chip = <Chip size="small" color="info" variant="outlined" label={`הוחזר${amount}`} />;
			break;
		case 'refund_failed':
			// the member was cancelled but PayMe refused the refund — someone has to refund by hand
			chip = <Chip size="small" color="error" label={`החזר נכשל${amount}`} />;
			break;
		default:
			chip = <Chip size="small" variant="outlined" label="חינם" />;
	}
	const title = p.payment_status === 'refund_failed' ? [tip, 'יש לבצע החזר ידני'].filter(Boolean).join(' · ') : tip;
	return title ? (
		<Tooltip title={title}>
			<span>{chip}</span>
		</Tooltip>
	) : (
		chip
	);
}
